import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Chip from "@mui/joy/Chip";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ColorPaletteProp } from "@mui/joy/styles";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Typography,
} from "@mui/joy";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Divider from "@mui/joy/Divider";
import { toast } from "react-toastify";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { db } from "../../FirebaseConfig";
import PersonIcon from "@mui/icons-material/Person";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import VerifiedIcon from "@mui/icons-material/Verified";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EngineeringIcon from "@mui/icons-material/Engineering";

interface Props {
  data: any;
  rowsPerPage: number;
}

type RoleType = "user" | "admin" | "super" | "organizer";

const PaginationTable: React.FC<Props> = ({ data, rowsPerPage }) => {
  const [currentPage, setCurrentPage] = React.useState(0);

  const RoleIcons: { [key in RoleType]: JSX.Element } = {
    user: <PersonIcon />,
    organizer: <EngineeringIcon />,
    admin: <ManageAccountsIcon />,
    super: <VerifiedIcon />,
  };

  const RoleColors: { [key in RoleType]: ColorPaletteProp } = {
    user: "success",
    organizer: "primary",
    admin: "danger",
    super: "warning",
  };

  const [open, setOpen] = React.useState(false);
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(
    null
  );

  const [currentRows, setCurrentRows] = React.useState(
    data.slice(0, rowsPerPage)
  );

  React.useEffect(() => {
    const indexOfLastRow = (currentPage + 1) * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    setCurrentRows(data.slice(indexOfFirstRow, indexOfLastRow));
  }, [data, currentPage, rowsPerPage]);

  const requestDeleteUser = (userId: string) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteUser = async () => {
    if (selectedUserId) {
      try {
        await db.collection("users").doc(selectedUserId).delete();
        toast.success("User deleted successfully");

        const updatedRows = currentRows.filter(
          (row: any) => row.email !== selectedUserId
        );

        setCurrentRows(updatedRows);

        setOpen(false);
      } catch (error) {
        toast.error("Error deleting user");
      }
    }
  };

  const handlePromote = async (email: string, currentRole: RoleType) => {
    try {
      const userRef = db.collection("users").doc(email);
      const docSnapshot = await userRef.get();

      if (!docSnapshot.exists) {
        toast.error("User not found");
        return;
      }

      let newRole: RoleType;

      switch (currentRole) {
        case "user":
          newRole = "admin";
          break;
        case "admin":
          newRole = "super";
          break;
        default:
          newRole = currentRole;
      }

      await userRef.update({ role: newRole });
      toast.success("User promoted successfully");

      const updatedRows = currentRows.map((row: any) => {
        if (row.email === email) {
          return { ...row, role: newRole };
        }
        return row;
      });

      setCurrentRows(updatedRows);
    } catch (error) {
      toast.error("Error promoting user");
    }
  };

  const handleRemoveOrganizer = async (email: string) => {
    try {
      const userRef = db.collection("users").doc(email);
      const docSnapshot = await userRef.get();

      if (!docSnapshot.exists) {
        toast.error("User not found");
        return;
      }

      await userRef.update({ role: "user" });
      toast.success("User removed from Organizer successfully");

      const updatedRows = currentRows.map((row: any) => {
        if (row.email === email) {
          return { ...row, role: "user" };
        }
        return row;
      });

      setCurrentRows(updatedRows);
    } catch (error) {
      toast.error("Error promoting user");
    }
  };

  const handleOrganizer = async (email: string) => {
    try {
      const userRef = db.collection("users").doc(email);
      const docSnapshot = await userRef.get();

      if (!docSnapshot.exists) {
        toast.error("User not found");
        return;
      }

      await userRef.update({ role: "organizer" });
      toast.success("User promoted to Organizer successfully");

      const updatedRows = currentRows.map((row: any) => {
        if (row.email === email) {
          return { ...row, role: "organizer" };
        }
        return row;
      });

      setCurrentRows(updatedRows);
    } catch (error) {
      toast.error("Error promoting user");
    }
  };

  const handleDemote = async (email: string, currentRole: RoleType) => {
    try {
      const userRef = db.collection("users").doc(email);
      const docSnapshot = await userRef.get();

      if (!docSnapshot.exists) {
        toast.error("User not found");
        return;
      }

      let newRole: RoleType;

      switch (currentRole) {
        case "admin":
          newRole = "user";
          break;
        case "super":
          newRole = "admin";
          break;
        default:
          newRole = currentRole;
      }

      await userRef.update({ role: newRole });
      toast.success("User demoted successfully");

      const updatedRows = currentRows.map((row: any) => {
        if (row.email === email) {
          return { ...row, role: newRole };
        }
        return row;
      });

      setCurrentRows(updatedRows);
    } catch (error) {
      toast.error("Error demoting user");
    }
  };

  const FirestoreTimestampToDate = ({
    seconds,
    nanoseconds,
  }: {
    seconds: any;
    nanoseconds: any;
  }) => {
    const milliseconds = seconds * 1000 + nanoseconds / 1000000; // Convert to milliseconds
    const date = new Date(milliseconds);
    const formattedDate = date.toLocaleDateString();
    return formattedDate;
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                User Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Email Id.
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Join Date
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>Role</th>
              <th
                style={{
                  width: "5%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                {" "}
              </th>
            </tr>
          </thead>

          <tbody>
            {currentRows?.map((row: any, index: any) => (
              <tr key={row.id}>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">{index + 1}</Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">{row.name ?? "None"}</Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">{row.email}</Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Typography level="body-xs">
                    {FirestoreTimestampToDate(row.joinedAt)}
                  </Typography>
                </td>
                <td style={{ textAlign: "center", padding: "12px 6px" }}>
                  <Chip
                    variant="soft"
                    size="sm"
                    startDecorator={RoleIcons[row.role as RoleType]}
                    color={RoleColors[row.role as RoleType]}
                  >
                    {row.role}
                  </Chip>
                </td>
                <td style={{ textAlign: "center" }}>
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{
                        root: {
                          variant: "plain",
                          color: "neutral",
                          size: "sm",
                        },
                      }}
                    >
                      <MoreHorizRoundedIcon />
                    </MenuButton>
                    <Menu size="sm" sx={{ minWidth: 140 }}>
                      <MenuItem
                        onClick={() => {
                          handlePromote(row.email, row.role as RoleType);
                        }}
                      >
                        Promote
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleDemote(row.email, row.role as RoleType);
                        }}
                      >
                        Demote
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleOrganizer(row.email);
                        }}
                      >
                        Make Organizer
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleRemoveOrganizer(row.email);
                        }}
                      >
                        Remove Organizer
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={() => {
                          requestDeleteUser(row.email);
                        }}
                        color="danger"
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>

      <div>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>
              <WarningRoundedIcon />
              Confirmation
            </DialogTitle>
            <Divider />
            <DialogContent>
              Are you sure you want to delete this User?
            </DialogContent>
            <DialogActions>
              <Button variant="plain" color="danger" onClick={handleDeleteUser}>
                Delete
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
};

export default PaginationTable;
